import { FormControl, FormField, FormItem, FormLabel, FormMessage } from '@repo/ui/components/form';
import { Input } from '@repo/ui/components/input';
import { useState } from 'react';
import { useFormContext } from 'react-hook-form';

import type { Clock } from '@/lib/clock';

import { fieldLabel, fieldSurface } from '../auth/AuthFrame';
import { slotDefaults } from './time-slot';

/**
 * From and to, prefilled with the clock's now and five minutes on (#277).
 *
 * Mounted only where the outlet asks and there is something to charge, and
 * each field **unregisters when it unmounts** — so the next party is prefilled
 * from the clock again rather than from the last one's window, and a counter
 * that does not ask sends no slot at all.
 */
export function TimeSlotFields({ clock, disabled }: { clock: Clock; disabled: boolean }) {
  const { control } = useFormContext<{ slotFrom?: string; slotTo?: string }>();
  // Read once, when the fields appear: a prefill that moved while somebody was
  // looking at it would be a window nobody chose.
  const [prefill] = useState(() => slotDefaults(clock()));

  return (
    <div className="flex shrink-0 gap-3">
      {(
        [
          ['slotFrom', 'Slot from', prefill.from],
          ['slotTo', 'Slot to', prefill.to],
        ] as const
      ).map(([name, label, value]) => (
        <FormField
          key={name}
          control={control}
          name={name}
          defaultValue={value}
          shouldUnregister
          render={({ field }) => (
            <FormItem className="w-36">
              <FormLabel required className={fieldLabel}>
                {label}
              </FormLabel>
              <FormControl
                render={
                  <Input
                    type="time"
                    className={`${fieldSurface} h-touch font-mono text-lg numeric md:text-lg`}
                    required
                    disabled={disabled}
                    {...field}
                    value={field.value ?? ''}
                  />
                }
              />
              <FormMessage />
            </FormItem>
          )}
        />
      ))}
    </div>
  );
}
